"use client";

import { memo, type CSSProperties, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import { isImportOrigin } from "@/components/ui/profile-origin";
import { cn } from "@/lib/utils";
import type { CandidateCellRow } from "./row";

interface NameCellProps {
  row: CandidateCellRow;
  /** When set, clicking the name calls this instead of bubbling to the row. */
  onClick?: (profileId: string) => void;
  /** Table-specific badges rendered after the origin badges (e.g. ATS provenance). */
  trailing?: ReactNode;
}

export const NameCell = memo(
  function NameCell({ row, onClick, trailing }: NameCellProps) {
    const displayName = row.fullName ?? "Unknown";
    return (
      <div
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation();
                onClick(row.profileId);
              }
            : undefined
        }
        className={cn(
          "flex min-w-0 items-center gap-3",
          onClick && "cursor-pointer group/name",
        )}
      >
        <CandidateAvatar
          name={displayName}
          avatarUrl={row.avatarUrl}
          size={22}
          initialsPx={9}
        />
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-sm font-medium text-heading",
            onClick && "text-left group-hover/name:underline",
          )}
        >
          {displayName}
        </span>
        {row.origin === "search" && (
          <Badge
            variant="linear"
            size="icon-sm"
            style={{ "--gradient-color": "var(--brand)" } as CSSProperties}
          >
            <Icon name="IconSearchIntelligence" size={12} className="text-brand" />
          </Badge>
        )}
        {isImportOrigin(row.origin) && (
          <Badge
            variant="linear"
            size="icon-sm"
            style={{ "--gradient-color": "#a78bfa" } as CSSProperties}
          >
            <Icon
              name="IconArrowOutOfBox"
              size={12}
              className="text-[#7c3aed] dark:text-[#a78bfa]"
            />
          </Badge>
        )}
        {trailing}
      </div>
    );
  },
  // `onClick` identity is ignored on purpose: the arbitrator passes a callback
  // that changes with every results update, and this memo exists to absorb
  // exactly that churn on large result sets. Only its presence affects output.
  (prev, next) =>
    prev.row.profileId === next.row.profileId &&
    prev.row.fullName === next.row.fullName &&
    prev.row.avatarUrl === next.row.avatarUrl &&
    prev.row.origin === next.row.origin &&
    !prev.onClick === !next.onClick &&
    prev.trailing === next.trailing,
);
